import { gql } from "@apollo/client";
import { format } from "date-fns";
import { getFields, getOrderByWithSecondOrder } from "../../util/graphqlUtil";
import { QUERY_PAGE_SIZE } from "../util";
import { IPagedPersonsQuery } from "../../interfaces/personer";
import { f } from "../../util/f";
import { PersonDtoFilterInput } from "../../interfaces/schema";
import {
  fmuretssagsproeveFields,
  foreningPersonFields,
  kursusPersonFields,
  personAbonnementFields,
  personBeregnetAdresseFields,
  personBeskikkelseFields,
  personBetalingsadresseFields,
  personBidragsopkraevningFields,
  personDisciplinaerstatusFields,
  personEfteruddannelseFields,
  personFields,
  personFirmaFields,
  personForsikringFields,
  personHistorik2Fields,
  personHistorikFields,
  personInteresseFields,
  personKlienterklaeringFields,
  personKontaktadresseFields,
  personKorrespondanceFields,
  personPersonartFields,
  personPersontypeFields,
  personPrincipalFields,
  personSanktionFields,
  personTilsynFields,
  personTilsynserklaeringFields,
  personUdenlandskadvokatsamfundFields,
  personUdvalgFields,
} from "./fields";

export const GET_PERSON_AUTOCOMPLETE_QUERY = (query: {
  q: string;
  erUdoevendeAdvokat?: boolean;
  erFmuretssagsproeveCensor?: boolean;
}) => {
  const { q, erUdoevendeAdvokat, erFmuretssagsproeveCensor } = query;
  const qNumber = parseInt(q, 10);
  const qSplit = q.split(" ");

  const whereClause = f<PersonDtoFilterInput>()
    .addFilter("and", [
      isNaN(qNumber)
        ? {
            and: [
              // eslint-disable-next-line no-unsafe-optional-chaining
              ...qSplit?.map((input) => {
                return {
                  fuldtNavn: {
                    contains: input,
                  },
                };
              }),
            ],
          }
        : {},
      !isNaN(qNumber)
        ? {
            kundeNr: {
              eq: qNumber,
            },
          }
        : {},
      erUdoevendeAdvokat
        ? {
            erUdoevende: {
              eq: true,
            },
          }
        : {},
      erFmuretssagsproeveCensor
        ? {
            personPersontype: {
              some: {
                and: [
                  {
                    persontypeNr: {
                      eq: 34,
                    },
                  },
                  {
                    periode: {
                      fraDato: { lte: format(new Date(), "yyyy-MM-dd") },
                      tilDatoInklusiv: {
                        gte: format(new Date(), "yyyy-MM-dd"),
                      },
                    },
                  },
                ],
              },
            },
          }
        : {},
    ])
    .build();

  return gql`
    {
      personer(
        first: 5,
        order: { fuldtNavn: ASC },
        where: ${whereClause}
      ) {
        edges {
          node {
            id
            kundeNr
            fuldtNavn
            erUdoevende
            personart
            personFirma {
              id
              periode {
                tilDatoInklusiv
              }
              firma {
                navn1
              }
            }
            primaerPersonFirmaRelation {
              id
              firma {
                navn1
              }
            }
          }
        }
      }
    }
  `;
};
export const GET_PERSONS_BY_FIRSTNAMES_LASTNAME = (query: {
  fornavne: string;
  efternavn: string;
}) => {
  const { efternavn, fornavne } = query;

  const clause = `and: [
    {fornavne: { eq: "${fornavne}" } }
    {efternavn: { eq: "${efternavn}" } }
  ]`;

  return gql`
    {
      personer(
      first: 50,
      where: {
      ${clause}
      }) {
        edges {
          node {
            id
            kundeNr
            fuldtNavn
            foedselsDato
            personFirma {
              id 
              periode {
                tilDatoInklusiv
              }
              firma {
                navn1
              }
            }
          }
        }
      }
    }
  `;
};

export const GET_PAGED_PERSONS_QUERY = (query: IPagedPersonsQuery) => {
  const { after, before, fields, filters } = query;
  const nodeFields = getFields(fields);
  const order = getOrderByWithSecondOrder(query, "kundeNr", "ASC");

  return gql`
    query pagedperson {
    personer(
        ${order}
        ${before ? "last: " + QUERY_PAGE_SIZE : "first: " + QUERY_PAGE_SIZE}
        ${after ? `after: "${after}"` : before ? `before: "${before}"` : ""}
        ${filters}
        ) {
        totalCount
        pageInfo {
          startCursor
          endCursor
          hasNextPage
          hasPreviousPage
        }
        edges {
          node {
            prioriteretEmail
            primaerPersonFirmaRelation {
              periode {
                tilDatoInklusiv
              }
            }
            ${nodeFields}
          }
        }
      }
    }
  `;
};

export const GET_PERSONER_IDS_AND_EMAIL = (
  query: IPagedPersonsQuery,
  totalCount: number,
) => {
  const { filters } = query;

  return gql`
    query personIds {
      personer(
        ${filters ? filters : ""}
        first: ${totalCount}
      ) {
        totalCount
        edges {
          node {
            id
            prioriteretEmail
          }
        }
      }
    }
  `;
};

export const FULDMAEGTIGE_FOR_PERSON_BY_ID_QUERY = gql`
  query fuldmaegtigeForPerson($id: UUID!) {
    person(id: $id) {
      id
      personPrincipalPersonPrincipalNavigation {
        id
        prioriteretEmail
        periode {
          fraDato
          tilDatoInklusiv
        }
      }
    }
  }
`;

export const PERSON_BY_ID_TICKETDATA = gql`
  query personById_ticketdata($id: UUID!) {
    person(id: $id) {
      id
      fuldtNavn
      kundeNr
      personFirma {
        id
        periode {
          fraDato
          tilDatoInklusiv
        }
        firma {
          navn1
        }
      }
    }
  }
`;

/**
 * Person stamdata
 */
const fieldMappings = {
  personFields,
  personBidragsopkraevning: `personBidragsopkraevning { ${personBidragsopkraevningFields} }`,
  personForsikring: `personForsikring { ${personForsikringFields} }`,
  adresse: `adresse { ${personBeregnetAdresseFields} }`,
  personPersonart: `personPersonart { ${personPersonartFields} }`,
  personUdenlandskadvokatsamfund: `personUdenlandskadvokatsamfund { ${personUdenlandskadvokatsamfundFields} }`,
  personBeskikkelse: `personBeskikkelse { ${personBeskikkelseFields} }`,
  primaerPersonFirmaRelation: `primaerPersonFirmaRelation { ${personFirmaFields} }`,
  personFirma: `personFirma { ${personFirmaFields} }`,
  personPrincipalPersonFuldm: `personPrincipalPersonFuldm { ${personPrincipalFields} }`,
  personPrincipalPersonPrincipalNavigation: `personPrincipalPersonPrincipalNavigation { ${personPrincipalFields} }`,
  personPersontype: `personPersontype { ${personPersontypeFields} }`,
  personInteresse: `personInteresse { ${personInteresseFields} }`,
  personIdKort: `personIdKort { id status opretDato ekspederingstidspunkt ekspederetAf }`,
  personKontaktadresse: `personKontaktadresse { ${personKontaktadresseFields} }`,
  personKorrespondance: `personKorrespondance { ${personKorrespondanceFields} }`,
  foreningPerson: `foreningPerson { ${foreningPersonFields} }`,
  personUdvalg: `personUdvalg { ${personUdvalgFields} }`,
  personhistorikNavigation: `personhistorikNavigation { ${personHistorikFields} }`,
  personhistorik2Navigation: `personhistorik2Navigation { ${personHistorik2Fields} }`,
  personAbonnement: `personAbonnement { ${personAbonnementFields} }`,
  personBetalingsadresse: `personBetalingsadresse { ${personBetalingsadresseFields} }`,
  personTilsynserklaering: `personTilsynserklaering { ${personTilsynserklaeringFields} }`,
  personTilsyn: `personTilsyn { ${personTilsynFields} }`,
  personEfteruddannelse: `personEfteruddannelse { ${personEfteruddannelseFields} }`,
  personKlienterklaering: `personKlienterklaering { ${personKlienterklaeringFields} }`,
  fmuretssagsproeveEksaminand: `fmuretssagsproeveEksaminand { ${fmuretssagsproeveFields} }`,
  fmuretssagsproeveCensor: `fmuretssagsproeveCensor { ${fmuretssagsproeveFields} }`,
  kursusPerson: `kursusPerson { ${kursusPersonFields} }`,
  personDisciplinaerstatus: `personDisciplinaerstatus { ${personDisciplinaerstatusFields} }`,
  personSanktion: `personSanktion { ${personSanktionFields} }`,
};
/**
 * Kun personens ID-kort-bestillinger.
 *
 * Findes for at kunne opdatere ID-kort-tabellen efter "Markér som sendt" uden at hente personen
 * forfra: personsidens hovedforespørgsel kører med notifyOnNetworkStatusChange, og et refetch
 * dér sætter loading — hvorefter usePersonChecks erstatter hele siden med en spinner og fanen
 * remounter. Denne forespørgsel rører ikke den tilstand.
 *
 * Feltlisten er med vilje den samme som fieldMappings.personIdKort.
 */
export const PERSON_IDKORT_QUERY = gql`
  query personIdKort($id: UUID!) {
    person(id: $id) {
      id
      personIdKort {
        id
        status
        opretDato
        ekspederingstidspunkt
        ekspederetAf
      }
    }
  }
`;

export const PERSON_BY_ID_QUERY = (
  queryName = "personById",
  options?: Partial<Record<keyof typeof fieldMappings, boolean>>,
) => {
  let queryParts: string[];

  if (options) {
    const effectiveOptions = {
      personFields: true,
      primaerPersonFirmaRelation: true,
      personPersonart: true,
      adresse: true,
      ...options,
    };

    queryParts = Object.entries(effectiveOptions)
      .filter(([, value]) => value)
      .map(([key]) => fieldMappings[key]);
  } else {
    queryParts = Object.values(fieldMappings);
  }

  return gql`
    query ${queryName}($id: UUID!${
      options?.fmuretssagsproeveCensor ? ",$dato: Date!" : ""
    }) {
      person(id: $id) {
        ${queryParts.join("\n")}
        ${
          options?.fmuretssagsproeveCensor
            ? `
            fmuretssagsproeveCensor {
              eksaminandNavigation {
                primaerPersonFirmaRelationPaa(dato: $dato) {
                  firma {
                    id
                    navn1
                  }
                }
              }
            }
            `
            : ""
        }
        ${
          options?.personPrincipalPersonFuldm
            ? `
            personPrincipalPersonFuldm {
              personPrincipalNavigation {
                personFirma {
                  id
                  priorieteretPrimaerFra
                  personfirmatypeNr
                  periode {
                    fraDato
                    tilDato
                  }
                }
              }
            }
            `
            : ""
        }
        ${
          options?.personPrincipalPersonPrincipalNavigation
            ? `
            personPrincipalPersonPrincipalNavigation {
              personPrincipalNavigation {
                personFirma {
                  id
                  priorieteretPrimaerFra
                  personfirmatypeNr
                  periode {
                    fraDato
                    tilDato
                  }
                }
              }
            }
            `
            : ""
        }
      }
    }
  `;
};
